import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { publicAuthApi } from '@/services/publicAuth.service';

type UnsubscribeState = 'working' | 'done' | 'error';

export interface UseUnsubscribePageResult {
  state: UnsubscribeState;
  message: string;
}

/**
 * Unsubscribing happens on load, not on a click.
 *
 * Someone who clicked "unsubscribe" has already told us what they want; making them confirm it
 * again is a dark pattern. The server call is idempotent, so a re-visit is harmless.
 */
export const useUnsubscribePage = (): UseUnsubscribePageResult => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('t') ?? '';

  const [state, setState] = useState<UnsubscribeState>('working');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('This unsubscribe link is missing its code. Please use the link from the email.');
      return;
    }

    let cancelled = false;
    publicAuthApi
      .unsubscribe(token)
      .then(() => {
        if (cancelled) return;
        setState('done');
        setMessage("You're unsubscribed. We won't email you about Shoutt again.");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState('error');
        setMessage(
          error instanceof Error ? error.message : 'That unsubscribe link is no longer valid.',
        );
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return { state, message };
};
